/**
 * Reason codes added by C2 (specs/C2-reason-codes/spec.md §4): reasons no C1 route declares,
 * reasons the engine reports through the bot, warnings and closing reasons. C1's codes live in
 * `contract-codes.ts` and are never repeated here. FR/EN texts: `src/i18n` (`reason.<CODE>`).
 *
 * Stability rule (C2 spec R1): a value is never renamed or reused once merged. Add values;
 * deprecate old ones in a comment. A code a C1 route starts to return moves to `contract-codes.ts`
 * with the same value.
 */

/** Platform: opening outside Telegram (client side, S2). */
export const PLATFORM_REASON_CODES = ['INIT_DATA_MISSING'] as const;

/** Telegram identification beyond C1's codes: the 2FA step text, returned by no route (C1 spec §5.4). */
export const TELEGRAM_REASON_CODES = ['TELEGRAM_PASSWORD_REQUIRED'] as const;

/** Channel states of FXM-02 §5 shown on E6, E7 and E12 (`CHANNEL_NOT_CONFIGURED` is C1's). */
export const CHANNEL_STATE_REASON_CODES = ['CHANNEL_PAUSED', 'CHANNEL_UNREACHABLE'] as const;

/** Settings and safety limits (FXM-02 §4–§6; M2); the codes of the settings routes are C1's. */
export const SETTINGS_REASON_CODES = [
  'DAILY_LOSS_LIMIT_APPROACHED',
  'DAILY_LOSS_LIMIT_REACHED',
  'DAILY_LOSS_LIMIT_ALREADY_EXCEEDED',
  'MAX_POSITIONS_REACHED',
] as const;

/** Signals and confirmation mode, mostly sent by the bot (FXM-02 §4, §5, §6 case 4). */
export const CONFIRMATION_REASON_CODES = [
  'SIGNAL_WITHOUT_STOP_LOSS',
  'CONFIRMATION_EXPIRED',
  'CONFIRMATION_CANCELLED',
  'CONFIRMATION_OBSOLETE',
  'CONFIRMATION_DECLINED',
] as const;

/** Emergency stop E10 (FXM-02 §4, §6 case 6; M4 R6, R10). */
export const EMERGENCY_STOP_REASON_CODES = [
  'EMERGENCY_PAUSE_FAILED',
  'POSITIONS_UNAVAILABLE',
] as const;

/** Execution failures reported by the engine (FXM-04 §4–§7, FXM-02 §6 cases 1–2; the last two are W4 R7 and R9, provisional until Q161 and Q162). */
export const EXECUTION_REASON_CODES = [
  'INSTRUMENT_NOT_FOUND',
  'INSTRUMENT_UNAVAILABLE',
  'MARKET_CLOSED',
  'SIZE_BELOW_MINIMUM',
  'SIZE_ABOVE_MAXIMUM',
  'SIZE_NOT_ON_STEP',
  'STOP_LOSS_ALREADY_PASSED',
  'INSUFFICIENT_MARGIN',
  'ORDER_REJECTED',
  'TRADING_ACCOUNT_SERVER_NOT_ALLOWED',
  'TRADING_ACCOUNT_NOT_RESPONDING',
  'MODIFICATION_REFUSED_BY_BROKER',
  'PARTIAL_CLOSE_ROUNDED_TO_FULL',
  'ACCOUNT_LIQUIDATED',
  'INSTRUCTION_MISSED',
  'CLOSE_DELAYED',
  'PRICE_GAP',
] as const;

/** Position tracking and timeline (FXM-04 §5, §6 case 14; M5). */
export const TRACKING_REASON_CODES = [
  'POSITION_TRACKING_LOST',
  'POSITION_MISMATCH',
  'TIMELINE_UNAVAILABLE',
] as const;

/**
 * Closing reasons shown in history E9 (FXM-04 §5 "Position"; FXM-02 §4; M6).
 * `CLOSED_BY_EMERGENCY_STOP` is provisional until Q71, `CLOSE_REASON_UNKNOWN` until Q56.
 */
export const CLOSING_REASON_CODES = [
  'CLOSED_BY_CHANNEL',
  'CLOSED_BY_STOP_LOSS',
  'CLOSED_BY_TAKE_PROFIT',
  'CLOSED_BY_TRADER',
  'CLOSED_BY_EMERGENCY_STOP',
  'CLOSE_REASON_UNKNOWN',
] as const;

/** All C2 groups, keyed by concern (used by tests and the README). */
export const CATALOGUE_REASON_CODE_GROUPS = {
  platform: PLATFORM_REASON_CODES,
  telegram: TELEGRAM_REASON_CODES,
  channelState: CHANNEL_STATE_REASON_CODES,
  settings: SETTINGS_REASON_CODES,
  confirmation: CONFIRMATION_REASON_CODES,
  emergencyStop: EMERGENCY_STOP_REASON_CODES,
  execution: EXECUTION_REASON_CODES,
  tracking: TRACKING_REASON_CODES,
  closing: CLOSING_REASON_CODES,
} as const;
